// submitLivestreamPaymentTracking.js
'use strict';
import getMeData from '../selector/getMeData.js';
import getLiveStreamData from '../selector/getLiveStreamData.js';
import getOperationData from '../selector/getOperationData.js';
import getExternalData from '../selector/getExternalData.js';
import getRouterData from '../selector/getRouterData.js';
import getStreamTokenDecodedData from '../selector/getStreamTokenDecodedData.js';
import getPrivateShowStatus from '../selector/getPrivateShowStatus.js';

import trackMixpanel, { getDataFromPath } from '../resource/mixpanel.js';

/**
 * Get the time offset seconds between server and client
 * @param {number} serverTimestamp - server timestamp
 * @param {number} clientTimestamp - client timestamp
 * @returns {number} - time offset seconds
 */
const getTimeOffsetSeconds = (serverTimestamp, clientTimestamp) => {
  if (serverTimestamp == null || clientTimestamp == null) {
    return;
  }
  return Math.floor((serverTimestamp - clientTimestamp) / 1000);
};

/**
 * Submit livestream payment tracking
 * @kind action
 * @param {object} payload - action payload.
 * @param {string} payload.streamId - stream id.
 * @param {string} payload.sessionId - streaming session id.
 * @param {string} payload.requestId - payment request id.
 * @param {string} payload.via - payment via.
 * @param {number} payload.price - payment price.
 * @param {number} payload.duration - payment duration.
 * @param {string} payload.giftId - private show ticket gift id.
 * @param {number} payload.count - private show ticket count.
 * @param {string} payload.modalId - the modal id that user trigger the payment.
 * @param {string} payload.privateTriggerType - private trigger type.
 * @param {import('../resource/mixpanel.js').EventTypes} payload.trackingType - payment tracking event type.
 * @param {object} payload.error - payment error object.
 * @return {Promise} Action promise.
 */
const submitLivestreamPaymentTracking =
  ({
    streamId,
    sessionId,
    requestId,
    via,
    price,
    duration,
    giftId,
    count,
    modalId,
    privateTriggerType,
    trackingType,
    error,
  } = {}) =>
  async (dispatch, getState) => {
    const state = getState();

    const { viewId } =
      getDataFromPath({
        pathname: getRouterData(state, ['location', 'pathname']),
      }) || {};

    const currentTokenData = getStreamTokenDecodedData(state, streamId);
    const meId = getMeData(state, 'id');

    const pusherPreset = getLiveStreamData(state, [streamId], 'pusherPreset');
    const pusherConnection = getOperationData(state, ['pusher'], 'connection');
    const pusherChannelSubscription = [
      `presence-enc-stream-viewer@${streamId}.preview.${meId}`,
      `presence-enc-stream-viewer@${streamId}.sd.${meId}`,
    ]
      .map(channel => ({
        channel,
        subscription: getOperationData(state, ['pusher', 'channels'], channel),
      }))
      .reduce((accumulated, { channel, subscription }) => {
        accumulated[`pusher.channel.${channel}`] = subscription;
        return accumulated;
      }, {});

    const activeStreamId = getOperationData(
      state,
      ['liveStream'],
      'activeStreamId'
    );
    const streamStatus = getLiveStreamData(state, [streamId], 'status');
    const privateShowStatus = getPrivateShowStatus(state, streamId);
    const fundingId = getLiveStreamData(
      state,
      [streamId, 'privateShow'],
      'fundingId'
    );
    const performingId = getLiveStreamData(
      state,
      [streamId, 'privateShow'],
      'performingId'
    );

    const clientTimestamp = getExternalData(state, ['timestamp'], 'client');
    const serverTimestamp = getExternalData(state, ['timestamp'], 'server');

    const playerStatus = getLiveStreamData(state, [streamId], 'playerStatus');
    const playerPreset = getLiveStreamData(state, [streamId], 'preset');

    const selectedTicketType = getOperationData(
      state,
      ['liveStream'],
      'selectedGiftType'
    );

    const payload = {
      'view.id': viewId,
      'modal.id': modalId,
      'stream.id': streamId,
      'stream.sessionId': sessionId,
      'stream.activeId': activeStreamId,
      'stream.status': streamStatus,
      'stream.privateShow.status': privateShowStatus,
      'stream.privateShow.fundingId': fundingId,
      'stream.privateShow.performingId': performingId,
      'time.server': serverTimestamp,
      'time.client': clientTimestamp,
      'time.offsetSeconds': getTimeOffsetSeconds(
        serverTimestamp,
        clientTimestamp
      ),
      'request.id': requestId,
      'token.current.nbf': currentTokenData?.nbf,
      'token.current.exp': currentTokenData?.exp,
      'player.status': playerStatus,
      'player.preset': playerPreset,
      'payment.price': price,
      'payment.via': via,
      'payment.duration': duration,
      'payment.privateShow.giftId': giftId,
      'payment.privateShow.count': count,
      'payment.privateShow.selectedTicketType': selectedTicketType,
      'payment.privateTriggerType': privateTriggerType,
      'pusher.preset': pusherPreset,
      'pusher.connection': pusherConnection,
      ...pusherChannelSubscription,
      'error.message': error?.message,
    };

    trackMixpanel({
      type: trackingType,
      payload,
    });
  };

export default submitLivestreamPaymentTracking;
